// src/pages/AdminCourses.tsx
import React, { useState, useEffect } from "react";
import {
  useGetAdminCoursesQuery,
  useDeleteCourseMutation,
  usePublishCourseMutation,
  useUnpublishCourseMutation,
} from "../../services/courses";
import { CreateCourseModal } from "../../components/admin/CreateCourseModal";
import { EditCourseModal } from "../../components/admin/EditCourseModal";
import ManageModulesModal from "../../components/admin/ManageModulesModal";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { loadFromStorage } from "../../slices/authSlice";

interface Course {
  _id: string;
  title: string;
  description: string;
  published: boolean;
  modules: Module[];
  createdAt: string;
}

interface Module {
  _id: string;
  title: string;
  description: string;
  order?: number;
  lessons: Lesson[];
}

interface Lesson {
  _id: string;
  title: string;
  description: string;
  videoPath: string;
  hasWorksheet: boolean;
  worksheetPath: string;
  duration?: number;
  order?: number;
}

const AdminCourses = () => {
  const dispatch = useAppDispatch();

  // Load auth from storage on component mount
  useEffect(() => {
    dispatch(loadFromStorage());
  }, [dispatch]);

  // Select auth state properly
  const authState = useAppSelector((state) => state.auth);
  const { role, token, email } = authState;

  console.log(email)

  // Debug logs
  console.log("Full auth state:", authState);
  console.log("Current role:", role);
  console.log("Has token:", !!token);
  console.log(
    "Token preview:",
    token ? `${token.substring(0, 20)}...` : "No token"
  );

  const { data: courses, isLoading, error } = useGetAdminCoursesQuery();
  const [deleteCourse] = useDeleteCourseMutation();
  const [publishCourse] = usePublishCourseMutation();
  const [unpublishCourse] = useUnpublishCourseMutation();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [managingCourse, setManagingCourse] = useState<Course | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleDelete = async (courseId: string, courseTitle: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${courseTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteCourse(courseId).unwrap();
        alert("Course deleted successfully");
      } catch (err) {
        alert("Failed to delete course");
        console.error(err);
      }
    }
  };

  const handleTogglePublish = async (course: Course) => {
    try {
      if (course.published) {
        await unpublishCourse(course._id).unwrap();
        alert("Course unpublished successfully");
      } else {
        await publishCourse(course._id).unwrap();
        alert("Course published successfully");
      }
    } catch (err) {
      alert("Failed to update course status");
      console.error(err);
    }
  };

  const filteredCourses = courses?.filter(
    (course: Course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading courses...</div>
      </div>
    );
  }

  if (error) {
    console.error("Error loading courses:", error);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-xl text-red-600 mb-4">Error loading courses</div>
          <p className="text-gray-600">
            {(error as any)?.data?.error ||
              "Failed to load courses. Please check your permissions."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Course Management
            </h1>
            <p className="text-gray-600 mt-1">
              Create and manage your courses, modules, and lessons
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
          >
            + Create New Course
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Total Courses</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {courses?.length || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Published</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {courses?.filter((c: Course) => c.published).length || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Draft</h3>
            <p className="text-3xl font-bold text-orange-600 mt-2">
              {courses?.filter((c: Course) => !c.published).length || 0}
            </p>
          </div>
        </div>

        {/* Courses Grid - Responsive with better breakpoints */}
        {filteredCourses && filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
            {filteredCourses.map((course: Course) => (
              <div
                key={course._id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col min-h-[420px]"
              >
                {/* Course Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Title and Status Badge */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-xl font-semibold text-gray-900 flex-1 leading-tight">
                      {course.title}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${
                        course.published
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {course.published ? "Published" : "Draft"}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-6 line-clamp-2 min-h-[40px]">
                    {course.description || "No description"}
                  </p>

                  {/* Course Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      <span>
                        <span className="font-medium text-gray-700">
                          {course.modules?.length || 0}
                        </span>{" "}
                        modules
                      </span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        <span className="font-medium text-gray-700">
                          {course.modules?.reduce(
                            (acc, mod) => acc + (mod.lessons?.length || 0),
                            0
                          ) || 0}
                        </span>{" "}
                        lessons
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons - Auto spacing */}
                  <div className="flex flex-col gap-2.5 mt-auto">
                    <button
                      onClick={() => setManagingCourse(course)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors text-sm"
                    >
                      Manage Content
                    </button>
                    <div className="grid grid-cols-2 gap-2.5">
                      <button
                        onClick={() => setEditingCourse(course)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2.5 rounded-lg font-medium transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleTogglePublish(course)}
                        className={`px-3 py-2.5 rounded-lg font-medium transition-colors text-sm ${
                          course.published
                            ? "bg-orange-100 hover:bg-orange-200 text-orange-700"
                            : "bg-green-100 hover:bg-green-200 text-green-700"
                        }`}
                      >
                        {course.published ? "Unpublish" : "Publish"}
                      </button>
                    </div>
                    <button
                      onClick={() => handleDelete(course._id, course.title)}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2.5 rounded-lg font-medium transition-colors text-sm border border-red-200"
                    >
                      Delete Course
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? "No courses found matching your search"
                : "No courses yet. Create your first course to get started!"}
            </p>
          </div>
        )}

        {/* Modals */}
        {showCreateModal && (
          <CreateCourseModal onClose={() => setShowCreateModal(false)} />
        )}

        {editingCourse && (
          <EditCourseModal
            course={editingCourse}
            onClose={() => setEditingCourse(null)}
          />
        )}

        {managingCourse && (
          <ManageModulesModal
            course={managingCourse}
            onClose={() => setManagingCourse(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminCourses;
